import { useEffect, useRef, useState, useCallback } from 'react';
import { AnamService } from '../../services/anamService';
import { useQuizStore } from '../../hooks';
import { ErrorMessage, LoadingData } from '../../ui';
import './AnamAvatar.css';

const POSITIVE_CONSENT_WORDS = ['sí', 'si', 'see', 'yes', 'yeah', 'yep', 'acepto', 'aceptar', 'claro', 'ok', 'okay', 'dale', 'bueno', 'de acuerdo'];
const NEGATIVE_CONSENT_WORDS = ['no', 'nope', 'nah', 'never', 'rechazar', 'rechazo', 'niego', 'jamás', 'nunca'];
const MAX_CONSENT_ATTEMPTS = 2;

export const AnamAvatarQuiz = () => {
  const {
    sessionId,
    initializeAnam,
    initializeChatBot,
    processAnamAnswer,
    getNextAnamQuestion,
    sendQuizAnswers,
    setTermsAccepted,
    setSessionClosed,
  } = useQuizStore();

  const noAnswerFallbackMessage = 'No pudimos obtener tu respuesta te invitamos a diligenciar el formulario por la via tradicional.';
  const completionConsentMessage = `¡Encuesta completada! Ahora necesito tu consentimiento para el tratamiento de datos.
Entiendes cómo se usarán tus datos y das el consentimiento para el tratamiento de mi información personal y de salud. ¿Aceptas? (Responde 'Sí' para continuar)`;
  const consentRejectedMessage = "Debes aceptar el consentimiento para continuar. ¿Aceptas el tratamiento de tus datos? (Responde 'Sí')";
  const goodbyeMessage = 'Gracias por completar la encuesta. A continuación podrás agendar tu valoración.';

  const introMessage = 'Hola. Soy Ana Lucía y te voy a acompañar a prepararte para tu valoración. Te haré algunas preguntas para que el especialista te conozca antes de la consulta. Terminando esta encuesta te invito a que agendes tu valoración';

  const [anamSessionToken, setAnamSessionToken] = useState('');
  const [isInitializing, setIsInitializing] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [currentQuestionText, setCurrentQuestionText] = useState('');
  const [error, setError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [displayIcon, setDisplayIcon] = useState('🎤');

  const anamServiceRef = useRef(null);
  const videoElementRef = useRef(null);
  const questionnaireFlowRef = useRef(false);
  const initializationInProgressRef = useRef(false);
  const hasInitializedRef = useRef(false);
  const hasAskedFirstQuestionRef = useRef(false);
  const isMountedRef = useRef(true);
  const sessionIdRef = useRef(sessionId);

  useEffect(() => {
    setSessionClosed(false);
  }, [setSessionClosed]);


  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  const isPositiveConsent = (answer) => {
    if (!answer || !answer.trim()) return false;
    const normalized = answer.toLowerCase().trim();
    return POSITIVE_CONSENT_WORDS.some(word => normalized.includes(word));
  };

  const isNegativeConsent = (answer) => {
    if (!answer || !answer.trim()) return false;
    const normalized = answer.toLowerCase().trim();
    return NEGATIVE_CONSENT_WORDS.some(word => normalized.includes(word));
  };

  const updateMicIcon = useCallback(() => {
    if (anamServiceRef.current) {
      const isMuted = anamServiceRef.current.isMicrophoneMuted();
      setDisplayIcon(isMuted === true ? '🔇' : '🎤');
    }
  }, []);

  const closeAnamSession = useCallback(async () => {
    try {
      if (anamServiceRef.current) {
        await anamServiceRef.current.disconnect();
        anamServiceRef.current = null;
      }
      localStorage.removeItem('anam_session_token');
      setAnamSessionToken('');
    } catch (err) {
      console.error('[ANAM] Error closing session:', err);
      localStorage.removeItem('anam_session_token');
    }
  }, []);

  const startQuestionnaireFlow = useCallback(async () => {
    if (questionnaireFlowRef.current || !anamServiceRef.current) {
      return;
    }

    questionnaireFlowRef.current = true;

    try {


      let waitAttempts = 0;
      const maxWaitAttempts = 50;
      while (!sessionIdRef.current && waitAttempts < maxWaitAttempts) {
        await new Promise(resolve => setTimeout(resolve, 100));
        waitAttempts++;
      }

      if (!sessionIdRef.current) {
        setError('Necesitamos inicializar la conversación primero');
        questionnaireFlowRef.current = false;
        return;
      }

      const stopMicrophone = () => { };


      await anamServiceRef.current.sendIntroMessage(introMessage, stopMicrophone);
      await new Promise(resolve => setTimeout(resolve, 2000));


      const handleConsentFlow = async () => {
        if (!anamServiceRef.current || !isMountedRef.current) {
          setError('Error: Servicio de avatar no disponible');
          return;
        }

        let consentAttempts = 0;
        const consentDeniedMessage = 'Entiendo. Sin tu consentimiento no podemos procesar tus datos. Te invitamos a diligenciar el formulario de forma tradicional.';

        try {
          consentAttempts++;
          let consentAnswer = await anamServiceRef.current.askConsentQuestion(
            completionConsentMessage,
            stopMicrophone,
            15,
            2
          );

          if (!isMountedRef.current) return;


          if (consentAnswer && isPositiveConsent(consentAnswer)) {
            setTermsAccepted(true);
            await sendQuizAnswers();


            await anamServiceRef.current.sendMessage(goodbyeMessage, stopMicrophone);
            await new Promise(resolve => setTimeout(resolve, 1000));

            setSessionClosed(true);
            await closeAnamSession();
            return;
          }


          if (consentAnswer && isNegativeConsent(consentAnswer)) {
            await anamServiceRef.current.sendFallbackMessage(consentDeniedMessage, stopMicrophone);
            if (!isMountedRef.current) return;
            setError('Consentimiento rechazado. Por favor completa el formulario de forma tradicional.');
            setSessionClosed(true);
            await closeAnamSession();
            return;
          }


          if (consentAnswer && consentAnswer.trim() && consentAttempts < MAX_CONSENT_ATTEMPTS) {
            consentAttempts++;
            consentAnswer = await anamServiceRef.current.askConsentQuestion(
              consentRejectedMessage,
              stopMicrophone,
              15,
              2
            );

            if (!isMountedRef.current) return;

            if (consentAnswer && isPositiveConsent(consentAnswer)) {
              setTermsAccepted(true);
              await sendQuizAnswers();


              await anamServiceRef.current.sendMessage(goodbyeMessage, stopMicrophone);
              await new Promise(resolve => setTimeout(resolve, 1000));

              setSessionClosed(true);
              await closeAnamSession();
              return;
            }


            if (consentAnswer && isNegativeConsent(consentAnswer)) {
              await anamServiceRef.current.sendFallbackMessage(consentDeniedMessage, stopMicrophone);
              if (!isMountedRef.current) return;
              setError('Consentimiento rechazado. Por favor completa el formulario de forma tradicional.');
              setSessionClosed(true);
              await closeAnamSession();
              return;
            }
          }

          if (!isMountedRef.current) return;
          setError(noAnswerFallbackMessage);
          setSessionClosed(true);
          await closeAnamSession();
        } catch (err) {
          console.error('[ANAM] Error with consent:', err);
          if (!isMountedRef.current) return;
          setError(err.message || 'Error al procesar consentimiento');
          await closeAnamSession();
        }
      };



      let langchainResponse = await getNextAnamQuestion("Quiero llenar la encuesta por mensajes de voz");

      if (!isMountedRef.current) {
        questionnaireFlowRef.current = false;
        return;
      }


      if (langchainResponse?.context?.is_complete) {
        await handleConsentFlow();
        questionnaireFlowRef.current = false;
        return;
      }


      while (langchainResponse && !langchainResponse.context?.is_complete) {
        if (!anamServiceRef.current || !isMountedRef.current) break;

        const questionText = langchainResponse.response;
        if (!questionText) break;


        setCurrentQuestionText(questionText);

        try {
          setIsProcessing(true);

          if (!isMountedRef.current) break;


          const answer = await anamServiceRef.current.askAssessmentQuestion(
            questionText,
            stopMicrophone,
            15,
            2,
            false
          );
          hasAskedFirstQuestionRef.current = true;

          if (!isMountedRef.current) break;

          if (answer && answer.trim()) {

            const expressionPromise = anamServiceRef.current?.sendShortExpression();


            langchainResponse = await processAnamAnswer(answer);

            if (!isMountedRef.current) break;


            if (expressionPromise) {
              await expressionPromise;
            }

            await new Promise(resolve => setTimeout(resolve, 100));
          } else {

            if (!isMountedRef.current) break;
            setError(noAnswerFallbackMessage);
            setSessionClosed(true);
            await closeAnamSession();
            break;
          }

          if (!isMountedRef.current) break;
          setIsProcessing(false);
        } catch (err) {
          console.error('[ANAM] Error asking question:', err);
          if (!isMountedRef.current) break;
          setError(err.message || 'Error al procesar pregunta');
          setIsProcessing(false);
          break;
        }
      }


      if (isMountedRef.current && langchainResponse?.context?.is_complete) {
        setCurrentQuestionText('');
        await handleConsentFlow();
      }

      questionnaireFlowRef.current = false;
    } catch (err) {
      console.error('[ANAM] Error in questionnaire flow:', err);
      setError(err.message || 'Error en el flujo de la encuesta');
      questionnaireFlowRef.current = false;
    }
  }, [processAnamAnswer, getNextAnamQuestion, sendQuizAnswers, setTermsAccepted, closeAnamSession, setSessionClosed]);

  const initializeAnamService = useCallback(async () => {
    if (anamServiceRef.current || !videoElementRef.current || !anamSessionToken || isInitializing) {
      return;
    }

    try {
      setIsInitializing(true);
      setError('');



      let retries = 0;
      const maxRetries = 20;
      while (!videoElementRef.current && retries < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, 100));
        retries++;
      }

      if (!videoElementRef.current) {
        throw new Error('Video element not available');
      }

      const anamService = new AnamService({
        onReady: async () => {
          if (!isMountedRef.current) return;
          setIsReady(true);
          setIsInitializing(false);
          await new Promise(resolve => setTimeout(resolve, 2000));
          if (!isMountedRef.current) return;
          startQuestionnaireFlow();
        },
        onError: (err) => {
          console.error('[ANAM] Service error:', err);
          if (!isMountedRef.current) return;
          setError(err.message || 'Error al inicializar avatar');
          setIsInitializing(false);
          localStorage.removeItem('anam_session_token');
        },
        onSpeechStart: () => {
          if (!isMountedRef.current) return;
          updateMicIcon();
          setTimeout(updateMicIcon, 100);
        },
        onSpeechEnd: () => {
          if (!isMountedRef.current) return;
          updateMicIcon();
          setTimeout(updateMicIcon, 100);
        },
        onMicStateChange: () => {
          if (!isMountedRef.current) return;
          updateMicIcon();
        },
      });

      anamServiceRef.current = anamService;
      await anamService.initialize(anamSessionToken, videoElementRef.current);
    } catch (err) {
      console.error('[ANAM] Failed to initialize:', err);
      setError(err?.message || 'Error al inicializar avatar');
      setIsInitializing(false);
      anamServiceRef.current = null;
      localStorage.removeItem('anam_session_token');
    }
  }, [anamSessionToken, isInitializing, startQuestionnaireFlow, updateMicIcon]);

  useEffect(() => {
    if (initializationInProgressRef.current || hasInitializedRef.current) {
      return;
    }

    const initializeEverything = async () => {
      initializationInProgressRef.current = true;

      try {

        if (!anamSessionToken) {
          const storedToken = localStorage.getItem('anam_session_token');
          if (storedToken) {
            setAnamSessionToken(storedToken);

          } else {
            const token = await initializeAnam();
            if (token) {
              localStorage.setItem('anam_session_token', token);
              setAnamSessionToken(token);
            }
          }
        }



        if (!sessionId) {
          await initializeChatBot();
        }

        hasInitializedRef.current = true;
      } catch (err) {
        console.error('[ANAM] Initialization error:', err);
        setError(err.message || 'Error al inicializar');
      } finally {
        initializationInProgressRef.current = false;
      }
    };

    initializeEverything();
  }, [anamSessionToken, sessionId, initializeAnam, initializeChatBot]);

  useEffect(() => {
    if (anamSessionToken && !anamServiceRef.current) {
      initializeAnamService();
    }
  }, [anamSessionToken, initializeAnamService]);

  useEffect(() => {
    if (!isReady || !anamServiceRef.current) {
      return;
    }

    const micStateInterval = setInterval(() => {
      updateMicIcon();
    }, 1000);

    return () => clearInterval(micStateInterval);
  }, [isReady, updateMicIcon]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (anamServiceRef.current) {
        anamServiceRef.current.disconnect().catch(console.error);
        anamServiceRef.current = null;
      }
      hasInitializedRef.current = false;
      initializationInProgressRef.current = false;
      questionnaireFlowRef.current = false;
      localStorage.removeItem('anam_session_token');
    };
  }, []);

  return (
    <>
      {isReady && (
        <div className="anam-session-status-wrapper">
          <div className="anam-session-status">
            <span className="anam-session-icon" aria-hidden="true">
              {displayIcon}
            </span>
          </div>
        </div>
      )}
      <div className="anam-avatar-quiz-container">
        <div className="text-center">
          <p className="text-muted mb-0">
            El avatar te hará preguntas. Responde con tu voz.
          </p>
        </div>

        {error && (
          <div className="mb-3">
            <ErrorMessage>{error}</ErrorMessage>
          </div>
        )}

        {(isInitializing || !isReady) && (
          <div className="text-center mb-3">
            <LoadingData />
            <p className="text-muted mt-2">Inicializando avatar...</p>
          </div>
        )}

        <div className="anam-avatar-wrapper mb-4">
          <div className="anam-avatar-container">
            <div className="anam-video-wrapper">
              <video
                ref={videoElementRef}
                autoPlay
                playsInline
                muted={false}
                className="anam-video"
              />
              {!isReady && (
                <div className="anam-loading-overlay">
                  <div className="loading-spinner"></div>
                  <p>Conectando al avatar...</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {currentQuestionText && isReady && (
          <div className="current-question-card card p-3 mb-3">
            <p className="mb-0 fw-semibold">{currentQuestionText}</p>
          </div>
        )}
      </div>
    </>
  );
};
